import React, { useState } from "react";
import { Box, Button, Group, Modal, Stack, Text, Textarea, Tooltip } from "@mantine/core";
import { ConfirmDialog } from "@nodus/ui";
import type { Comment, CommentVisibility } from "./types";

/**
 * A single comment row, Reddit-style — meta line + body + inline
 * action strip. The connector line down the left of nested replies
 * is drawn by the wrapping ``CommentThread`` recursion, not here.
 */

export interface CommentRowProps {
  comment: Comment;
  onSave(id: number, body: string): Promise<void> | void;
  onDelete(id: number, force?: boolean): Promise<void> | void;
  onReply?(parentId: number, parentVisibility: Comment["visibility"]): void;
  /** Staff-only: flip the comment's visibility. The button is only
   *  rendered when this callback is provided; ``CommentThread``
   *  gates that on the backend-supplied ``can_change_visibility``
   *  flag so non-staff users never see it. */
  onChangeVisibility?(id: number, visibility: CommentVisibility): Promise<void> | void;
}

const formatAbsoluteTime = (iso: string): string => {
  // ``YYYY-MM-DD HH:MM:SS`` in the viewer's local timezone — surfaced
  // in the "edited" tooltip so the reader can see exactly when the
  // body was last changed, not just the dimmed relative hint.
  let d: Date;
  try {
    d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
  } catch {
    return iso;
  }
  const pad = (n: number) => n.toString().padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
};

const formatRelativeTime = (iso: string): string => {
  // Reddit-style: "just now" / "5m ago" / "2h ago" / "3d ago" /
  // "Mar 14" / "Mar 14, 2024". Falls back to the raw ISO if the
  // string can't be parsed.
  let d: Date;
  try {
    d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
  } catch {
    return iso;
  }
  const seconds = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (seconds < 30) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  // Older than a week — give the absolute date. Year is only shown
  // when it's a different year from now.
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
};

const VISIBILITY_LABEL: Record<Comment["visibility"], string> = {
  admin: "staff only",
  public: "tenant-visible",
};

export const CommentRow: React.FC<CommentRowProps> = ({
  comment,
  onSave,
  onDelete,
  onReply,
  onChangeVisibility,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const [busy, setBusy] = useState(false);
  const [visibilityModalOpen, setVisibilityModalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleSave = async () => {
    if (!draft.trim() || draft.trim() === comment.body) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await onSave(comment.id, draft.trim());
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  // The cascade is the whole reason this confirmation exists, so it is what the
  // dialog leads with. `confirm()` used to carry it (#341): a native modal that
  // ignores the theme, cannot be styled or tested, and blocks the whole tab.
  const deleteDescription =
    comment.reply_count > 0
      ? `This comment and its ${comment.reply_count} ${
          comment.reply_count === 1 ? "reply" : "replies"
        } will be deleted. This cannot be undone.`
      : "This comment will be deleted. This cannot be undone.";

  const handleDelete = async () => {
    setBusy(true);
    try {
      // Force-cascade when there are replies (only staff can do this;
      // backend rejects 409 otherwise — caller surfaces).
      await onDelete(comment.id, comment.reply_count > 0);
      setDeleteOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const isAdmin = comment.visibility === "admin";

  return (
    <Stack gap={4} py={4}>
      <Group gap={6} wrap="nowrap" align="baseline">
        <Text size="sm" fw={600}>
          {comment.author_name || "Unknown"}
        </Text>
        <Text size="xs" c="dimmed">
          ·
        </Text>
        <Text size="xs" c="dimmed">
          {formatRelativeTime(comment.created_at)}
        </Text>
        {comment.edited_at && (
          <>
            <Text size="xs" c="dimmed">
              ·
            </Text>
            <Tooltip label={formatAbsoluteTime(comment.edited_at)} withArrow>
              <Text size="xs" c="dimmed" fs="italic" style={{ cursor: "help" }}>
                edited
              </Text>
            </Tooltip>
          </>
        )}
        <Text size="xs" c="dimmed">
          ·
        </Text>
        <Text size="xs" c={isAdmin ? "blue.6" : "green.7"} fw={500}>
          {VISIBILITY_LABEL[comment.visibility]}
        </Text>
      </Group>

      {editing ? (
        <Stack gap={6} mt={2}>
          <Textarea
            autosize
            minRows={2}
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            disabled={busy}
          />
          <Group gap="xs" justify="flex-end">
            <Button
              size="xs"
              variant="default"
              onClick={() => {
                setDraft(comment.body);
                setEditing(false);
              }}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button size="xs" onClick={handleSave} loading={busy}>
              Save
            </Button>
          </Group>
        </Stack>
      ) : (
        <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
          {comment.body}
        </Text>
      )}

      {!editing && (
        <Group gap={2} mt={2}>
          {comment.can_reply && onReply && (
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              onClick={() => onReply(comment.id, comment.visibility)}
              disabled={busy}
            >
              Reply
            </Button>
          )}
          {comment.can_edit && (
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              onClick={() => setEditing(true)}
              disabled={busy}
            >
              Edit
            </Button>
          )}
          {comment.can_delete && (
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              onClick={() => setDeleteOpen(true)}
              disabled={busy}
            >
              Delete
            </Button>
          )}
          {onChangeVisibility && (
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              onClick={() => setVisibilityModalOpen(true)}
              disabled={busy}
            >
              {isAdmin ? "Make tenant-visible" : "Make staff-only"}
            </Button>
          )}
        </Group>
      )}
      <Box />
      {/* The repo's own confirmation, not the browser's — same dialog, wording
          and destructive-button treatment as every other delete in the app. */}
      <ConfirmDialog
        opened={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title={comment.reply_count > 0 ? "Delete comment and replies" : "Delete comment"}
        description={deleteDescription}
        onHardDelete={handleDelete}
        hardDeleteLabel="Delete"
        isSubmitting={busy}
      />
      {onChangeVisibility && (
        <Modal
          opened={visibilityModalOpen}
          onClose={() => setVisibilityModalOpen(false)}
          title="Change comment visibility"
          centered
          size="sm"
        >
          <Stack gap="md">
            <Text size="sm">
              {isAdmin ? (
                <>
                  This comment is currently <Text component="span" fw={600}>staff-only</Text>.
                  Making it <Text component="span" fw={600}>tenant-visible</Text> means tenants of the target row will
                  be able to read it. Continue?
                </>
              ) : (
                <>
                  This comment is currently <Text component="span" fw={600}>tenant-visible</Text>.
                  Making it <Text component="span" fw={600}>staff-only</Text> will hide it from tenants on their
                  next reload.
                  {comment.reply_count > 0 && (
                    <>
                      {" "}
                      <Text component="span" fw={600}>
                        Any tenant-visible replies will also be hidden
                      </Text>
                      {" "}
                      to keep the thread consistent.
                    </>
                  )}
                  {" "}Continue?
                </>
              )}
            </Text>
            <Group justify="flex-end" gap="xs">
              <Button
                variant="default"
                onClick={() => setVisibilityModalOpen(false)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button
                color={isAdmin ? "green" : "orange"}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await onChangeVisibility(
                      comment.id,
                      isAdmin ? "public" : "admin",
                    );
                    setVisibilityModalOpen(false);
                  } finally {
                    setBusy(false);
                  }
                }}
                loading={busy}
              >
                {isAdmin ? "Make tenant-visible" : "Make staff-only"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}
    </Stack>
  );
};
